import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Send, Plus, Minus, RefreshCw, FileText, X, Upload, Globe, Gamepad2, Map, Ship, Monitor, Calendar, Target, AlertTriangle, Cpu, Users, Zap, Box, CheckCircle, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { GameSelector } from "@/components/GameSelector";

const CLASSIFICATIONS = [
  "UI", "Combat", "AI", "Navigation", "Physics", "Rendering",
  "Performance", "Crash", "Mission Logic", "Progression", "Controls",
  "Network", "Audio", "Other"
];

const HOLOSIM_CLASSIFICATIONS = [
  "UI", "Fleet System", "Resource Economy", "Combat System",
  "Map / Sector", "Session / Login", "Browser Compatibility", "Backend Sync"
];

const HOLOSIM_ACTION_TYPES = [
  "Mining", "Combat", "Pilot", "Data Running", "Fleet Movement",
  "Crafting", "Market", "Crew", "Tutorial", "Mission"
];

const HOLOSIM_FACTIONS = ["MUD", "ONI", "USTUR"];

const SEVERITIES = ["Critical", "High", "Medium", "Low"];
const FREQUENCIES = ["Always", "Sometimes", "Rare"];
const VERSION_TYPES = ["DEV", "LIVE"];
const BROWSERS = ["Chrome", "Firefox", "Safari", "Edge", "Opera", "Brave", "Other"];
const OPERATING_SYSTEMS = ["Windows", "MacOS", "Linux", "Android", "iOS", "Other"];
const DEVICE_TYPES = ["Desktop", "Mobile", "Tablet"];

export default function SubmitBug() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("ai");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const manualFileInputRef = useRef(null);

  // Game selection state - NEW
  const [selectedGame, setSelectedGame] = useState("STAR_ATLAS_UE5");
  const isHolosim = selectedGame === "STAR_ATLAS_HOLOSIM";

  // Game hierarchy state
  const [gameHierarchy, setGameHierarchy] = useState({});
  const [categories, setCategories] = useState({});
  const [categoryType, setCategoryType] = useState("standard"); // standard, mission, season
  const [modes, setModes] = useState([]);
  const [maps, setMaps] = useState([]);
  const [missions, setMissions] = useState([]);
  const [seasons, setSeasons] = useState([]);

  // Ship database state
  const [shipDatabase, setShipDatabase] = useState({});
  const [shipSizes, setShipSizes] = useState([]);
  const [selectedShipSize, setSelectedShipSize] = useState("");
  const [availableShips, setAvailableShips] = useState([]);

  // AI Analysis Form
  const [rawReport, setRawReport] = useState("");
  const [aiImages, setAiImages] = useState([]);
  const [aiVideoUrl, setAiVideoUrl] = useState("");
  const [aiVideoData, setAiVideoData] = useState(null);

  // Manual Form - Basic
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [classification, setClassification] = useState("");
  const [severity, setSeverity] = useState("");
  const [frequency, setFrequency] = useState("Sometimes");
  
  // Manual Form - Game Hierarchy
  const [category, setCategory] = useState("");
  const [mode, setMode] = useState("");
  const [selectedMaps, setSelectedMaps] = useState([]);
  const [selectedMission, setSelectedMission] = useState("");
  const [selectedSeason, setSelectedSeason] = useState("");
  
  // Manual Form - Version
  const [version, setVersion] = useState("");
  const [versionType, setVersionType] = useState("DEV");
  
  // Manual Form - Ship
  const [shipModel, setShipModel] = useState("");
  const [shipSize, setShipSize] = useState("");
  
  // Manual Form - Environment (UE5)
  const [showEnvironment, setShowEnvironment] = useState(false);
  const [pcSpecs, setPcSpecs] = useState("");
  const [windowsVersion, setWindowsVersion] = useState("");
  const [gpu, setGpu] = useState("");
  const [ram, setRam] = useState("");
  
  // HOLOSIM-SPECIFIC FIELDS - NEW
  const [browser, setBrowser] = useState("");
  const [operatingSystem, setOperatingSystem] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [actionType, setActionType] = useState("");
  const [fleetComposition, setFleetComposition] = useState("");
  const [shipCount, setShipCount] = useState("");
  const [fleetPower, setFleetPower] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [miningLocation, setMiningLocation] = useState("");
  const [productionRate, setProductionRate] = useState("");
  const [starSystem, setStarSystem] = useState("");
  const [sector, setSector] = useState("");
  const [factionControl, setFactionControl] = useState("");
  
  // Holosim ID state
  const [holosimIdJson, setHolosimIdJson] = useState("");
  const [holosimIdParsed, setHolosimIdParsed] = useState(null);
  const [holosimIdError, setHolosimIdError] = useState("");
  
  // Manual Form - Steps
  const [steps, setSteps] = useState([""]);
  const [stepIds, setStepIds] = useState(() => [`step-${Date.now()}`]);
  const [expectedResult, setExpectedResult] = useState("");
  const [manualImages, setManualImages] = useState([]);
  const [manualVideoUrl, setManualVideoUrl] = useState("");
  const [manualVideoData, setManualVideoData] = useState(null);

  // Fetch game hierarchy and ships on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hierarchyRes, shipsRes] = await Promise.all([
          axios.get(`${API}/game-hierarchy`),
          axios.get(`${API}/ships`)
        ]);
        setGameHierarchy(hierarchyRes.data);
        setShipDatabase(shipsRes.data);
        setShipSizes(Object.keys(shipsRes.data));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Fetch categories when game changes
  useEffect(() => {
    const fetchGameCategories = async () => {
      try {
        const response = await axios.get(`${API}/games/${selectedGame}/categories`);
        setCategories(response.data);
        // Reset category when game changes
        setCategory("");
        setMode("");
        setSelectedMaps([]);
        setSelectedMission("");
        setSelectedSeason("");
        // Reset classification since options differ between games
        setClassification("");
      } catch (error) {
        console.error("Error fetching game categories:", error);
        // Fallback to all categories
        const allCategories = {};
        Object.entries(gameHierarchy).forEach(([cat, data]) => {
          allCategories[cat] = { abbr: data.abbr, type: data.type || "standard" };
        });
        setCategories(allCategories);
      }
    };
    if (selectedGame) {
      fetchGameCategories();
    }
  }, [selectedGame, gameHierarchy]);

  // Update when category changes - handle different types
  useEffect(() => {
    const updateCategoryData = async () => {
      if (!category) {
        setCategoryType("standard");
        setModes([]);
        setMaps([]);
        setMissions([]);
        setSeasons([]);
        return;
      }

      try {
        const response = await axios.get(`${API}/modes/${category}`);
        const data = response.data;
        
        setCategoryType(data.type);
        
        if (data.type === "mission") {
          setMissions(data.missions || []);
          setModes([]);
          setMaps([]);
          setSeasons([]);
        } else if (data.type === "season") {
          setSeasons(data.seasons || []);
          setModes([]);
          setMaps([]);
          setMissions([]);
        } else {
          setModes(data.modes || []);
          setMissions([]);
          setSeasons([]);
        }
        
        // Reset selections
        setMode("");
        setSelectedMaps([]);
        setSelectedMission("");
        setSelectedSeason("");
      } catch (error) {
        console.error("Error fetching category data:", error);
      }
    };
    
    updateCategoryData();
  }, [category, selectedGame]);

  // Set Holosim default season when seasons are loaded
  useEffect(() => {
    if (selectedGame === "STAR_ATLAS_HOLOSIM" && seasons.length > 0 && seasons.includes("Chapter 2") && !selectedSeason) {
      setSelectedSeason("Chapter 2");
    }
  }, [seasons, selectedGame, selectedSeason]);

  // Update maps when mode changes (for standard categories)
  useEffect(() => {
    const fetchMaps = async () => {
      if (categoryType !== "standard" || !category || !mode) {
        setMaps([]);
        return;
      }

      try {
        const response = await axios.get(`${API}/maps/${category}/${mode}`);
        setMaps(response.data || []);
        setSelectedMaps([]);
      } catch (error) {
        console.error("Error fetching maps:", error);
        setMaps([]);
      }
    };
    
    fetchMaps();
  }, [mode, category, categoryType]);

  // Update available ships when ship size changes
  useEffect(() => {
    if (selectedShipSize && shipDatabase[selectedShipSize]) {
      setAvailableShips(shipDatabase[selectedShipSize].ships || []);
    } else {
      setAvailableShips([]);
    }
  }, [selectedShipSize, shipDatabase]);

  const handleMapToggle = (mapName) => {
    setSelectedMaps(prev => {
      if (prev.includes(mapName)) {
        return prev.filter(m => m !== mapName);
      } else {
        return [...prev, mapName];
      }
    });
  };

  const parseHolosimId = () => {
    setHolosimIdError("");
    setHolosimIdParsed(null);
    if (!holosimIdJson.trim()) {
      setHolosimIdError("Please paste your Holosim ID JSON");
      return;
    }
    try {
      const parsed = JSON.parse(holosimIdJson);
      if (!parsed.gameId || !parsed.playerName) {
        setHolosimIdError("JSON must contain at least 'gameId' and 'playerName'");
        return;
      }
      setHolosimIdParsed(parsed);
    } catch (e) {
      setHolosimIdError("Invalid JSON format. Please check your input.");
    }
  };

  const handleSelectAllMaps = () => {
    if (selectedMaps.length === maps.length) {
      setSelectedMaps([]);
    } else {
      setSelectedMaps([...maps]);
    }
  };

  const handleImageUpload = async (e, setImages) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImages((prev) => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    }
    
    e.target.value = '';
  };

  const removeImage = (index, setImages) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVideoUpload = (e, setVideoData) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setVideoData(event.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ─── Clipboard paste handler ──────────────────────────────────────────────
  // Reads image/video blobs from clipboard. Inserts [Image N] reference at the
  // cursor in the target textarea and appends the media to the attachment list.
  // Plain-text pastes fall through to the browser's default behaviour.
  const makePasteHandler = useCallback(
    (getText, setText, getImages, setImages, setVideoData) =>
      (e) => {
        const items = Array.from(e.clipboardData?.items ?? []);
        const mediaItem = items.find(
          (it) => it.type.startsWith("image/") || it.type.startsWith("video/")
        );
        if (!mediaItem) return; // nothing visual — let text paste happen normally

        e.preventDefault();
        const blob = mediaItem.getAsFile();
        if (!blob) {
          toast.error("Could not read clipboard data. Try saving as a file and uploading instead.");
          return;
        }

        const isVideo = mediaItem.type.startsWith("video/");
        const maxBytes = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
        if (blob.size > maxBytes) {
          toast.error(
            isVideo
              ? "Video too large to paste (max 50 MB). Use the file upload instead."
              : "Image too large to paste (max 5 MB). Try compressing it first."
          );
          return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
          const dataUri = ev.target.result;
          if (isVideo) {
            setVideoData(dataUri);
            toast.success("Video pasted as attachment.");
            return;
          }

          // Image: insert [Image N] reference at cursor and add to attachment list
          setImages((prev) => {
            const newImages = [...prev, dataUri];
            const n = newImages.length;
            const ta = e.target;
            const start = ta.selectionStart ?? getText().length;
            const end = ta.selectionEnd ?? start;
            const marker = `[Image ${n}]`;
            const current = getText();
            setText(current.slice(0, start) + marker + current.slice(end));
            // Restore cursor after the marker on next tick
            requestAnimationFrame(() => {
              if (ta.setSelectionRange) {
                ta.setSelectionRange(start + marker.length, start + marker.length);
              }
            });
            return newImages;
          });
          toast.success("Screenshot pasted — see attachment strip below.");
        };
        reader.onerror = () => toast.error("Failed to read clipboard data.");
        reader.readAsDataURL(blob);
      },
    []
  );

  // Pre-bound paste handlers for each tab
  const handleAiPaste = makePasteHandler(
    () => rawReport, setRawReport,
    () => aiImages, setAiImages, setAiVideoData
  );
  const handleManualPaste = makePasteHandler(
    () => description, setDescription,
    () => manualImages, setManualImages, setManualVideoData
  );

  const handleAISubmit = async (e) => {
    e.preventDefault();
    if (!rawReport.trim()) {
      toast.error(t('submitBug.rawBugReport') + " is required");
      return;
    }
    
    // Validate Holosim ID for Holosim games
    if (isHolosim && !holosimIdParsed) {
      toast.error("Holosim ID is required for Holosim bug reports. Please paste and parse your ID.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        raw_report: rawReport,
        source: "AI Analysis",
        images: aiImages,
        video_url: aiVideoData || aiVideoUrl || null,
        game: selectedGame,
      };
      if (isHolosim && holosimIdParsed) {
        payload.holosim_id_json = JSON.stringify(holosimIdParsed);
      }
      const response = await axios.post(`${API}/bugs/analyze`, payload);
      toast.success("Thank you for reporting this bug! Your report has been submitted successfully.", { duration: 5000 });
      navigate(`/bugs/${response.data.id}`);
    } catch (error) {
      console.error("Error creating bug:", error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!category) {
      toast.error("Game Category is required");
      return;
    }
    
    // Validate based on category type
    if (categoryType === "standard") {
      if (!mode) {
        toast.error("Game Mode is required");
        return;
      }
      if (selectedMaps.length === 0) {
        toast.error("At least one Map is required");
        return;
      }
    } else if (categoryType === "mission") {
      if (!selectedMission) {
        toast.error("Mission is required");
        return;
      }
    } else if (categoryType === "season") {
      if (!selectedSeason) {
        toast.error("Season is required");
        return;
      }
    }
    
    if (!version.trim()) {
      toast.error("Game Version is required");
      return;
    }
    if (!classification) {
      toast.error("Classification is required");
      return;
    }
    if (!severity) {
      toast.error("Severity is required");
      return;
    }
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }
    
    // Validate Holosim ID for Holosim games
    if (isHolosim && !holosimIdParsed) {
      toast.error("Holosim ID is required for Holosim bug reports. Please paste and parse your ID.");
      return;
    }

    try {
      setLoading(true);
      
      const bugData = {
        title,
        description,
        category,
        classification,
        severity,
        frequency,
        version: isHolosim ? version : `${versionType}${version}`,
        version_type: isHolosim ? "" : versionType,
        ship_model: shipModel || null,
        ship_size: shipSize || null,
        steps_to_reproduce: steps.filter((s) => s.trim()),
        expected_result: expectedResult || "",
        images: manualImages,
        video_url: manualVideoData || manualVideoUrl || null,
        game: selectedGame,  // Include game selection
      };

      // Add type-specific fields
      if (categoryType === "standard") {
        bugData.mode = mode;
        bugData.maps = selectedMaps;
      } else if (categoryType === "mission") {
        bugData.mission = selectedMission;
      } else if (categoryType === "season") {
        bugData.season = selectedSeason;
      }

      // Add UE5 environment if provided
      if (!isHolosim && showEnvironment && (pcSpecs || windowsVersion || gpu || ram)) {
        bugData.environment = {
          pc: pcSpecs || null,
          windows_version: windowsVersion || null,
          gpu: gpu || null,
          ram: ram || null,
        };
      }

      // Add HOLOSIM-specific fields
      if (isHolosim) {
        bugData.browser = browser || null;
        bugData.operating_system = operatingSystem || null;
        bugData.device_type = deviceType || null;
        bugData.action_type = actionType || null;
        bugData.fleet_composition = fleetComposition || null;
        bugData.ship_count = shipCount ? parseInt(shipCount) : null;
        bugData.fleet_power = fleetPower ? parseInt(fleetPower) : null;
        bugData.resource_type = resourceType || null;
        bugData.mining_location = miningLocation || null;
        bugData.production_rate = productionRate || null;
        bugData.star_system = starSystem || null;
        bugData.sector = sector || null;
        bugData.faction_control = factionControl || null;
        // Holosim ID parsed fields
        if (holosimIdParsed) {
          bugData.holosim_game_id = holosimIdParsed.gameId;
          bugData.holosim_player_name = holosimIdParsed.playerName;
          bugData.holosim_player_profile = holosimIdParsed.playerProfile || null;
          bugData.holosim_fleets = holosimIdParsed.myFleets || null;
        }
      }

      const response = await axios.post(`${API}/bugs/manual`, bugData);
      toast.success("Thank you for reporting this bug! Your report has been submitted successfully.", { duration: 5000 });
      navigate(`/bugs/${response.data.id}`);
    } catch (error) {
      console.error("Error creating bug:", error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, ""]);
    setStepIds([...stepIds, `step-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`]);
  };
  const removeStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
    setStepIds(stepIds.filter((_, i) => i !== index));
  };
  const updateStep = (index, value) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
  };

  const ImagePreview = ({ images, setImages, inputRef }) => (
    <div className="mt-4">
      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
        {t('submitBug.attachImages')}
      </label>
      <div className="flex flex-wrap gap-3">
        {images.map((img, i) => (
          <div key={`img-${i}-${(img || '').slice(0, 32)}`} className="relative group">
            <img
              src={img}
              alt={`Attachment ${i + 1}`}
              className="w-24 h-24 object-cover rounded-sm border border-border/40"
            />
            {/* Number badge matches [Image N] markers in the report text */}
            <span className="absolute top-1 left-1 bg-black/70 text-white text-[9px] font-mono px-1 rounded leading-tight">
              {i + 1}
            </span>
            <button
              type="button"
              onClick={() => removeImage(i, setImages)}
              className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-24 h-24 border-2 border-dashed border-border/40 rounded-sm flex flex-col items-center justify-center text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
        >
          <Upload className="w-6 h-6 mb-1" />
          <span className="text-[10px] font-mono">{t('submitBug.uploadImage')}</span>
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground font-mono mt-2">{t('submitBug.maxFileSize')}</p>
    </div>
  );

  // Render game location section based on category type
  const renderGameLocationFields = () => {
    if (categoryType === "mission") {
      return (
        <div className="mt-4">
          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block flex items-center gap-2">
            <Target className="w-3 h-3" />
            Mission *
          </label>
          <Select value={selectedMission} onValueChange={setSelectedMission}>
            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-mission">
              <SelectValue placeholder="Select mission" />
            </SelectTrigger>
            <SelectContent>
              {missions.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (categoryType === "season") {
      return (
        <div className="mt-4">
          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            Season *
          </label>
          <Select value={selectedSeason} onValueChange={setSelectedSeason}>
            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-season">
              <SelectValue placeholder="Select season" />
            </SelectTrigger>
            <SelectContent>
              {seasons.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Standard category - Mode and Maps
    return (
      <>
        <div className="mt-4">
          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Mode *</label>
          <Select value={mode} onValueChange={setMode} disabled={!category}>
            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-mode">
              <SelectValue placeholder={category ? "Select mode" : "Select category first"} />
            </SelectTrigger>
            <SelectContent>
              {modes.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Map Selection */}
        {maps.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider flex items-center gap-2">
                <Map className="w-3 h-3" />
                Maps *
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectAllMaps}
                className="text-xs font-mono h-6 px-2"
              >
                {selectedMaps.length === maps.length ? "Deselect All" : "Select All"}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {maps.map((mapName) => (
                <label
                  key={mapName}
                  className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer transition-colors ${
                    selectedMaps.includes(mapName)
                      ? "bg-primary/20 border-primary/50"
                      : "bg-black/20 border-border/40 hover:border-border"
                  }`}
                >
                  <Checkbox
                    checked={selectedMaps.includes(mapName)}
                    onCheckedChange={() => handleMapToggle(mapName)}
                    data-testid={`map-${mapName.toLowerCase().replace(/\s/g, '-')}`}
                  />
                  <span className="text-xs font-mono">{mapName}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in" data-testid="submit-bug-page">
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleImageUpload(e, setAiImages)}
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
        multiple
        className="hidden"
      />
      <input
        type="file"
        ref={manualFileInputRef}
        onChange={(e) => handleImageUpload(e, setManualImages)}
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
        multiple
        className="hidden"
      />

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground">{t('submitBug.title')}</h1>
        <div className="cyber-line mt-3" />
        <p className="text-muted-foreground mt-3 font-body text-sm md:text-base">{t('submitBug.subtitle')}</p>
      </div>

      {/* Game Selector */}
      <div className="max-w-4xl mx-auto mb-6">
        <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-3 block flex items-center gap-2">
          <Gamepad2 className="w-4 h-4" />
          Select Game
        </label>
        <GameSelector 
          selectedGame={selectedGame} 
          onGameChange={setSelectedGame}
          size="large"
        />
      </div>

      <Card className="bg-card border-border/40 max-w-4xl mx-auto">
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full rounded-none border-b border-border/40 bg-muted/20 p-0 h-auto">
              <TabsTrigger
                value="ai"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-4 font-heading font-bold uppercase tracking-wider text-xs md:text-sm"
                data-testid="tab-ai"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('submitBug.aiAnalysis')}
              </TabsTrigger>
              <TabsTrigger
                value="manual"
                className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-4 font-heading font-bold uppercase tracking-wider text-xs md:text-sm"
                data-testid="tab-manual"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('submitBug.manualEntry')}
              </TabsTrigger>
            </TabsList>

            {/* AI Analysis Tab */}
            <TabsContent value="ai" className="p-4 md:p-6 m-0">
              <form onSubmit={handleAISubmit} className="space-y-6">
                <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm text-foreground font-body">
                        {t('submitBug.aiDescription')}
                      </p>
                      <ul className="text-xs text-muted-foreground mt-2 space-y-1 font-mono">
                        <li>• {t('submitBug.aiFeature1')}</li>
                        <li>• {t('submitBug.aiFeature2')}</li>
                        <li>• {t('submitBug.aiFeature3')}</li>
                        <li>• {t('submitBug.aiFeature4')}</li>
                        <li>• {t('submitBug.aiFeature5')}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Required Format Reference */}
                <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-sm p-4">
                  <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-yellow-400 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Required Report Format
                  </h4>
                  <div className="text-xs text-muted-foreground font-mono space-y-3">
                    <div className="bg-black/30 p-3 rounded-sm border border-border/40">
                      <p className="text-yellow-300 mb-2 font-bold">REQUIRED FIELDS (must include):</p>
                      <p><span className="text-foreground">Title:</span> Short descriptive bug title</p>
                      <p><span className="text-foreground">Description:</span> Clear explanation of what happens</p>
                      <p><span className="text-foreground">Game Version:</span> {isHolosim ? "e.g., v1.2.1" : "Live / Dev (e.g., Live 2026.02.13.111)"}</p>
                    </div>
                    
                    {isHolosim ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-cyan-400 font-bold mb-1">HOLOSIM CHAPTERS</p>
                          <p className="text-[10px]">Chapter 1, Chapter 2, Chapter 3</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-purple-400 font-bold mb-1">CLASSIFICATIONS</p>
                          <p className="text-[10px]">UI, Fleet System, Resource Economy, Combat System, Map / Sector, Session / Login, Browser Compatibility, Backend Sync</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-green-400 font-bold mb-1">FACTIONS / SECTORS</p>
                          <p className="text-[10px]">MUD, ONI, USTUR (each with 17 star systems)</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-orange-400 font-bold mb-1">ACTION TYPES</p>
                          <p className="text-[10px]">Mining, Combat, Pilot, Data Running, Fleet Movement, Crafting, Market, Crew, Tutorial, Mission</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-cyan-400 font-bold mb-1">SAGE / HOLOSIM</p>
                          <p className="text-[10px]">Category only (no Mode/Map)</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-purple-400 font-bold mb-1">GALIA</p>
                          <p className="text-[10px]">Missions: M1 Jungle, M2 Bouvi, M3 Norshoul, M4 Letrat, M5 Arcol, M6, M7</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-red-400 font-bold mb-1">SHOOTER</p>
                          <p className="text-[10px]">Modes: SURGE, GUN GAME, DEATHMATCH, TEAM DEATHMATCH, FFA ROCKETS, TEAM ROCKETS, ZONES, GUNPLAY TRAINER</p>
                          <p className="text-[10px]">Maps: GATEWAY, PAIZUL ARENA, URU</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-yellow-400 font-bold mb-1">RACING</p>
                          <p className="text-[10px]">GROUND: VOLANT STUDIO, EXINADE, CYNOSURE</p>
                          <p className="text-[10px]">AIR: VOLANT STUDIO, TRAINER STATION</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-blue-400 font-bold mb-1">DOGFIGHT</p>
                          <p className="text-[10px]">SIEGE: LAR-4 | TRAINING: TRAINER STATION</p>
                        </div>
                        <div className="bg-black/20 p-2 rounded-sm">
                          <p className="text-orange-400 font-bold mb-1">EXPLORE</p>
                          <p className="text-[10px]">Maps: VOLANT STATION, ONI C.S.S</p>
                        </div>
                      </div>
                    )}
                    
                    <p className="text-muted-foreground/70 italic">Optional: {isHolosim ? "Browser, Fleet Info, Resource Type, Star System, Sector" : "Ship Model, Steps to Reproduce, Media Attachments"}</p>
                  </div>
                </div>

                {/* Translation Note */}
                <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-sm p-3 flex items-center gap-3">
                  <Globe className="w-5 h-5 text-cyan-400" />
                  <p className="text-xs text-cyan-300 font-mono">{t('submitBug.translationNote')}</p>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                    {t('submitBug.rawBugReport')} *
                  </label>
                  <Textarea
                    value={rawReport}
                    onChange={(e) => setRawReport(e.target.value)}
                    onPaste={handleAiPaste}
                    placeholder={isHolosim 
                      ? `Example Holosim Report:

In HOLOSIM Chapter 2 mission 'Building Bridges' on version v1.2.1, after delivering 10,000 Food to the delivery target, the mission objective/progress UI does not update correctly.

Subsequent deliveries of Ammo and Fuel also don't reflect in the progress tracker.

Browser: Chrome
Star System: MRZ-16
Sector: MUD`
                      : `Example Report Format:

Title: Ship clips through wall near checkpoint
Description: My Ranger ship clipped through a wall and got stuck inside geometry when boosting near corners.
Game Version: DEV 2026.02.28.001

Category: SHOOTER
Mode: SURGE
Map: GATEWAY

Ship Model: Ranger

Steps to Reproduce:
1. Start SURGE mode on GATEWAY
2. Boost near corner walls
3. Ship clips through geometry

Expected: Ship should collide with walls normally`}
                    className="min-h-[250px] bg-black/20 border-border/50 focus:border-primary font-mono text-sm"
                    data-testid="raw-report-input"
                  />
                  <p className="text-[10px] text-muted-foreground font-mono mt-1 flex items-center gap-1">
                    <ClipboardPaste className="w-3 h-3" />
                    Tip: paste screenshots directly with Ctrl+V / Cmd+V
                  </p>
                </div>

                {/* Video Upload */}
                <div>
                  <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                    Video Evidence (Optional)
                  </label>
                  {aiVideoData ? (
                    <div className="relative">
                      <video src={aiVideoData} controls className="w-full max-h-48 rounded-sm border border-border/40 bg-black" />
                      <button
                        type="button"
                        onClick={() => setAiVideoData(null)}
                        className="absolute top-2 right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime"
                        onChange={(e) => handleVideoUpload(e, setAiVideoData)}
                        className="hidden"
                        id="ai-video-upload"
                      />
                      <label
                        htmlFor="ai-video-upload"
                        className="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-border/40 rounded-sm text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors cursor-pointer"
                      >
                        <Upload className="w-4 h-4" />
                        <span className="text-xs font-mono">Upload Video</span>
                      </label>
                      <Input
                        value={aiVideoUrl}
                        onChange={(e) => setAiVideoUrl(e.target.value)}
                        placeholder="Or paste video URL (YouTube, Discord...)"
                        className="bg-black/20 border-border/50 focus:border-primary font-mono text-xs"
                        data-testid="ai-video-url"
                      />
                    </div>
                  )}
                </div>

                {/* Image Upload */}
                <ImagePreview images={aiImages} setImages={setAiImages} inputRef={fileInputRef} />

                {/* Holosim ID for AI Tab */}
                {isHolosim && (
                  <div className="border border-amber-400/30 rounded-md p-4 space-y-3">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-amber-400/80 flex items-center gap-2">
                      <ClipboardPaste className="w-4 h-4" />
                      Holosim ID (Required)
                    </h3>
                    <Textarea
                      placeholder={'Paste your Holosim ID JSON here...\n{\n  "gameId": "...",\n  "playerName": "...",\n  "playerProfile": "...",\n  "myFleets": [...]\n}'}
                      value={holosimIdJson}
                      onChange={(e) => {
                        setHolosimIdJson(e.target.value);
                        setHolosimIdParsed(null);
                        setHolosimIdError("");
                      }}
                      className="bg-black/20 border-border/50 font-mono text-xs min-h-[100px]"
                      data-testid="ai-holosim-id-json-input"
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={parseHolosimId}
                        className="border-amber-400/50 text-amber-400 hover:bg-amber-400/10"
                        data-testid="ai-holosim-id-parse-btn"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Parse & Preview
                      </Button>
                      {holosimIdError && (
                        <span className="text-xs text-red-400 font-mono">{holosimIdError}</span>
                      )}
                    </div>
                    {holosimIdParsed && (
                      <div className="bg-emerald-950/30 border border-emerald-400/30 rounded-md p-3" data-testid="ai-holosim-id-preview">
                        <p className="text-xs text-emerald-400 font-bold uppercase tracking-wider mb-2">Parsed Successfully</p>
                        <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                          <div><span className="text-muted-foreground">Game ID:</span> {holosimIdParsed.gameId}</div>
                          <div><span className="text-muted-foreground">Player:</span> {holosimIdParsed.playerName}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full font-heading font-bold uppercase tracking-wider py-6"
                  data-testid="ai-submit-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      {t('submitBug.analyzing')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      {t('submitBug.analyzeBtn')}
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Manual Entry Tab */}
            <TabsContent value="manual" className="p-4 md:p-6 m-0">
              <form onSubmit={handleManualSubmit} className="space-y-6">
                
                {/* Section: Basic Info */}
                <div className="border-b border-border/40 pb-4 mb-4">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Basic Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('submitBug.bugTitle')} *</label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('submitBug.titlePlaceholder')}
                        className="bg-black/20 border-border/50 focus:border-primary"
                        data-testid="manual-title-input"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugDetail.description')} *</label>
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onPaste={handleManualPaste}
                        placeholder={t('submitBug.descriptionPlaceholder')}
                        className="min-h-[100px] bg-black/20 border-border/50 focus:border-primary"
                        data-testid="manual-description-input"
                      />
                      <p className="text-[10px] text-muted-foreground font-mono mt-1 flex items-center gap-1">
                        <ClipboardPaste className="w-3 h-3" />
                        Tip: paste screenshots directly with Ctrl+V / Cmd+V
                      </p>
                    </div>
                  </div>
                </div>

                {/* Section: Game Location */}
                <div className="border-b border-border/40 pb-4 mb-4">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                    <Gamepad2 className="w-4 h-4" />
                    Game Location
                  </h3>
                  
                  <div>
                    <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Category *</label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(categories).map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Dynamic fields based on category type */}
                  {category && renderGameLocationFields()}
                </div>

                {/* Section: Classification & Severity */}
                <div className="border-b border-border/40 pb-4 mb-4">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
                    Classification & Severity
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.classification')} *</label>
                      <Select value={classification} onValueChange={setClassification}>
                        <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-classification">
                          <SelectValue placeholder={t('bugList.all')} />
                        </SelectTrigger>
                        <SelectContent>
                          {(isHolosim ? HOLOSIM_CLASSIFICATIONS : CLASSIFICATIONS).map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.severity')} *</label>
                      <Select value={severity} onValueChange={setSeverity}>
                        <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-severity">
                          <SelectValue placeholder={t('bugList.all')} />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITIES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Frequency *</label>
                      <Select value={frequency} onValueChange={setFrequency}>
                        <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-frequency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCIES.map((f) => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Section: Version */}
                <div className="border-b border-border/40 pb-4 mb-4">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
                    Game Version
                  </h3>
                  
                  {isHolosim ? (
                    /* Holosim Version - just version number, no DEV/LIVE */
                    <div>
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Version *</label>
                      <Input
                        value={version}
                        onChange={(e) => setVersion(e.target.value)}
                        placeholder="v1.2.1"
                        className="bg-black/20 border-border/50 focus:border-primary font-mono"
                        data-testid="manual-version-input"
                      />
                      <p className="text-[10px] text-muted-foreground font-mono mt-1">Holosim uses version numbers only (no DEV/LIVE)</p>
                    </div>
                  ) : (
                    /* UE5 Version - DEV/LIVE + version number */
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Type *</label>
                        <Select value={versionType} onValueChange={setVersionType}>
                          <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-version-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VERSION_TYPES.map((vt) => (
                              <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-2">
                        <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Version Number *</label>
                        <Input
                          value={version}
                          onChange={(e) => setVersion(e.target.value)}
                          placeholder="2026.02.13.111"
                          className="bg-black/20 border-border/50 focus:border-primary font-mono"
                          data-testid="manual-version-input"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Section: Ship (Optional) */}
                <div className="border-b border-border/40 pb-4 mb-4">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                    <Ship className="w-4 h-4" />
                    Ship Information (Optional)
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Ship Size</label>
                      <Select value={selectedShipSize || "none"} onValueChange={(v) => {
                        setSelectedShipSize(v === "none" ? "" : v);
                        setShipSize(v === "none" ? "" : v);
                        setShipModel("");
                      }}>
                        <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-ship-size">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="all">All Ships</SelectItem>
                          {shipSizes.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2">
                      <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Ship Model</label>
                      {availableShips.length > 0 ? (
                        <Select value={shipModel || "none"} onValueChange={(v) => setShipModel(v === "none" ? "" : v)}>
                          <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="manual-ship-model">
                            <SelectValue placeholder="Select ship" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {availableShips.map((ship) => (
                              <SelectItem key={ship} value={ship}>{ship}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={shipModel}
                          onChange={(e) => setShipModel(e.target.value)}
                          placeholder="e.g., FBLBRA, OPAL, or custom ship name"
                          className="bg-black/20 border-border/50 focus:border-primary font-mono"
                          data-testid="manual-ship-model-input"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Section: Environment (Optional - for Discord ticket resolutions) */}
                {isHolosim ? (
                  /* HOLOSIM-SPECIFIC FIELDS */
                  <div className="border-b border-border/40 pb-4 mb-4 space-y-6">
                    {/* Holosim ID (Required) */}
                    <div>
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-amber-400/80 mb-4 flex items-center gap-2">
                        <ClipboardPaste className="w-4 h-4" />
                        Holosim ID (Required)
                      </h3>
                      <div className="space-y-3">
                        <Textarea
                          placeholder={'Paste your Holosim ID JSON here...\n{\n  "gameId": "...",\n  "playerName": "...",\n  "playerProfile": "...",\n  "myFleets": [...]\n}'}
                          value={holosimIdJson}
                          onChange={(e) => {
                            setHolosimIdJson(e.target.value);
                            setHolosimIdParsed(null);
                            setHolosimIdError("");
                          }}
                          className="bg-black/20 border-border/50 font-mono text-xs min-h-[120px]"
                          data-testid="holosim-id-json-input"
                        />
                        <div className="flex items-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={parseHolosimId}
                            className="border-amber-400/50 text-amber-400 hover:bg-amber-400/10"
                            data-testid="holosim-id-parse-btn"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Parse & Preview
                          </Button>
                          {holosimIdError && (
                            <span className="text-xs text-red-400 font-mono">{holosimIdError}</span>
                          )}
                        </div>
                        {holosimIdParsed && (
                          <div className="bg-emerald-950/30 border border-emerald-400/30 rounded-md p-3 space-y-1" data-testid="holosim-id-preview">
                            <p className="text-xs text-emerald-400 font-bold uppercase tracking-wider mb-2">Parsed Successfully</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
                              <div>
                                <span className="text-muted-foreground">Game ID:</span>{" "}
                                <span className="text-foreground">{holosimIdParsed.gameId}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Player:</span>{" "}
                                <span className="text-foreground">{holosimIdParsed.playerName}</span>
                              </div>
                              {holosimIdParsed.playerProfile && (
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">Profile:</span>{" "}
                                  <span className="text-foreground">{holosimIdParsed.playerProfile}</span>
                                </div>
                              )}
                              {holosimIdParsed.myFleets && holosimIdParsed.myFleets.length > 0 && (
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">Fleets:</span>{" "}
                                  <span className="text-foreground">{holosimIdParsed.myFleets.length} fleet(s) detected</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Browser Environment */}
                    <div>
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-cyan-400/80 mb-4 flex items-center gap-2">
                        <Monitor className="w-4 h-4" />
                        Browser Environment (Optional)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Browser</label>
                          <Select value={browser || "none"} onValueChange={(v) => setBrowser(v === "none" ? "" : v)}>
                            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="holosim-browser">
                              <SelectValue placeholder="Select browser" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not specified</SelectItem>
                              {BROWSERS.map((b) => (
                                <SelectItem key={b} value={b}>{b}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Operating System</label>
                          <Select value={operatingSystem || "none"} onValueChange={(v) => setOperatingSystem(v === "none" ? "" : v)}>
                            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="holosim-os">
                              <SelectValue placeholder="Select OS" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not specified</SelectItem>
                              {OPERATING_SYSTEMS.map((os) => (
                                <SelectItem key={os} value={os}>{os}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Device Type</label>
                          <Select value={deviceType || "none"} onValueChange={(v) => setDeviceType(v === "none" ? "" : v)}>
                            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="holosim-device">
                              <SelectValue placeholder="Select device" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not specified</SelectItem>
                              {DEVICE_TYPES.map((d) => (
                                <SelectItem key={d} value={d}>{d}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Action Type */}
                    <div>
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-cyan-400/80 mb-4 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Action Context (Optional)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Action Type</label>
                          <Select value={actionType || "none"} onValueChange={(v) => setActionType(v === "none" ? "" : v)}>
                            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="holosim-action">
                              <SelectValue placeholder="Select action" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not specified</SelectItem>
                              {HOLOSIM_ACTION_TYPES.map((a) => (
                                <SelectItem key={a} value={a}>{a}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Fleet Info */}
                    <div>
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-cyan-400/80 mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Fleet Information (Optional)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Fleet Composition</label>
                          <Input
                            value={fleetComposition}
                            onChange={(e) => setFleetComposition(e.target.value)}
                            placeholder="e.g., 3 miners, 2 fighters"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="holosim-fleet-comp"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Ship Count</label>
                          <Input
                            type="number"
                            value={shipCount}
                            onChange={(e) => setShipCount(e.target.value)}
                            placeholder="0"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="holosim-ship-count"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Fleet Power</label>
                          <Input
                            type="number"
                            value={fleetPower}
                            onChange={(e) => setFleetPower(e.target.value)}
                            placeholder="0"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="holosim-fleet-power"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Resource & Location Info */}
                    <div>
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-cyan-400/80 mb-4 flex items-center gap-2">
                        <Box className="w-4 h-4" />
                        Resource & Location (Optional)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Resource Type</label>
                          <Input
                            value={resourceType}
                            onChange={(e) => setResourceType(e.target.value)}
                            placeholder="e.g., Hydrogen, Carbon"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="holosim-resource"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Star System</label>
                          <Input
                            value={starSystem}
                            onChange={(e) => setStarSystem(e.target.value)}
                            placeholder="e.g., MRZ-7"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="holosim-system"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Sector (Faction)</label>
                          <Select value={sector || "none"} onValueChange={(v) => setSector(v === "none" ? "" : v)}>
                            <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="holosim-sector">
                              <SelectValue placeholder="Select faction sector" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not specified</SelectItem>
                              {HOLOSIM_FACTIONS.map((f) => (
                                <SelectItem key={f} value={f}>{f}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* UE5 ENVIRONMENT FIELDS */
                  <div className="border-b border-border/40 pb-4 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                        <Monitor className="w-4 h-4" />
                        Environment (Optional)
                      </h3>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowEnvironment(!showEnvironment)}
                        className="text-xs font-mono"
                      >
                        {showEnvironment ? "Hide" : "Show"}
                      </Button>
                    </div>
                    
                    {showEnvironment && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">PC Specs</label>
                          <Input
                            value={pcSpecs}
                            onChange={(e) => setPcSpecs(e.target.value)}
                            placeholder="e.g., Intel i7-12700K"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="manual-pc-specs"
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">Windows Version</label>
                          <Input
                            value={windowsVersion}
                            onChange={(e) => setWindowsVersion(e.target.value)}
                            placeholder="e.g., Windows 11 23H2"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="manual-windows"
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">GPU</label>
                          <Input
                            value={gpu}
                            onChange={(e) => setGpu(e.target.value)}
                            placeholder="e.g., RTX 4080"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="manual-gpu"
                          />
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">RAM</label>
                          <Input
                            value={ram}
                            onChange={(e) => setRam(e.target.value)}
                            placeholder="e.g., 32GB DDR5"
                            className="bg-black/20 border-border/50 focus:border-primary font-mono"
                            data-testid="manual-ram"
                          />
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground font-mono mt-2">
                      Required for bugs previously resolved via Discord ticket
                    </p>
                  </div>
                )}

                {/* Section: Steps to Reproduce */}
                <div className="border-b border-border/40 pb-4 mb-4">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
                    Steps to Reproduce
                  </h3>
                  
                  <div className="space-y-2">
                    {steps.map((step, i) => (
                      <div key={stepIds[i] || `step-${i}`} className="flex items-center gap-2">
                        <span className="text-muted-foreground font-mono text-sm w-6">{i + 1}.</span>
                        <Input
                          value={step}
                          onChange={(e) => updateStep(i, e.target.value)}
                          placeholder={`${t('submitBug.stepPlaceholder')} ${i + 1}`}
                          className="flex-1 bg-black/20 border-border/50 focus:border-primary"
                          data-testid={`step-input-${i}`}
                        />
                        {steps.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeStep(i)} className="text-muted-foreground hover:text-destructive">
                            <Minus className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addStep} className="font-mono text-xs" data-testid="add-step-btn">
                      <Plus className="w-4 h-4 mr-1" /> {t('submitBug.addStep')}
                    </Button>
                  </div>

                  <div className="mt-4">
                    <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugDetail.expectedResult')}</label>
                    <Textarea
                      value={expectedResult}
                      onChange={(e) => setExpectedResult(e.target.value)}
                      placeholder={t('submitBug.expectedPlaceholder')}
                      className="min-h-[80px] bg-black/20 border-border/50 focus:border-primary"
                      data-testid="expected-result-input"
                    />
                  </div>
                </div>

                {/* Section: Media */}
                <div className="mb-4">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
                    Media Attachments
                  </h3>

                  {/* Video Upload */}
                  <div className="mb-4">
                    <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                      Video Evidence (Optional)
                    </label>
                    {manualVideoData ? (
                      <div className="relative">
                        <video src={manualVideoData} controls className="w-full max-h-48 rounded-sm border border-border/40 bg-black" />
                        <button
                          type="button"
                          onClick={() => setManualVideoData(null)}
                          className="absolute top-2 right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/quicktime"
                          onChange={(e) => handleVideoUpload(e, setManualVideoData)}
                          className="hidden"
                          id="manual-video-upload"
                        />
                        <label
                          htmlFor="manual-video-upload"
                          className="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-border/40 rounded-sm text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors cursor-pointer"
                        >
                          <Upload className="w-4 h-4" />
                          <span className="text-xs font-mono">Upload Video</span>
                        </label>
                        <Input
                          value={manualVideoUrl}
                          onChange={(e) => setManualVideoUrl(e.target.value)}
                          placeholder="Or paste video URL (YouTube, Discord...)"
                          className="bg-black/20 border-border/50 focus:border-primary font-mono text-xs"
                          data-testid="manual-video-url"
                        />
                      </div>
                    )}
                  </div>

                  {/* Image Upload */}
                  <ImagePreview images={manualImages} setImages={setManualImages} inputRef={manualFileInputRef} />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full font-heading font-bold uppercase tracking-wider py-6"
                  data-testid="manual-submit-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      {t('submitBug.creating')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {t('submitBug.createBtn')}
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
